import { HookObject, ListItemsHooks } from "@/plugins/types";
import { ScreenIcon, ReviewsNavIcon } from "./navIcons";

import { Home } from "./screens/Home";
import { Campaigns } from "./screens/Campaigns";
import { Calendar } from "./screens/Calendar";
import { Meetings } from "./screens/Meetings";
import { Tasks } from "./screens/Tasks";
import { Digest } from "./screens/Digest";
import { Reviews } from "./screens/Reviews";
import { KnowledgeBase } from "./screens/KnowledgeBase";
import { TranscriptView } from "./screens/TranscriptView";
import { LiveRoom } from "./screens/LiveRoom";
import { AgentActivity } from "./screens/AgentActivity";
import { Settings } from "./screens/Settings";
import { Onboarding } from "./screens/Onboarding";
import { ClientPortal } from "./screens/ClientPortal";
import { installAgentsAuth } from "./api";

// AMB-432 — install the operator-API auth interceptor as soon as the Agents plugin loads,
// so every operator call (from any screen) forwards the platform session token.
installAgentsAuth();

/**
 * Launch OS Agents — operator product (supersedes the archived Concierge "Client OS").
 * Contributes the app-drawer nav entry + all screen routes via the plugin hook system.
 * Permission key: "agents". Screens are ported from the v8 prototype; data is still
 * mock — real-data wiring (Client-Docs vault per the frontmatter contract) is next.
 */

const PERMISSION = "agents";

export const onListItems = (
  hook: HookObject,
  _payload: any,
  _actions: any,
  { permissions = [] }: { permissions?: string[] } = {}
) => {
  // Left app-drawer entries — each primary screen is its own top-level icon.
  // The robot is simply Home. Decimal orders keep the Agents icons grouped
  // together (contiguous after order-1 apps) regardless of other permissions.
  if (hook.id === ListItemsHooks.APP_DRAWER && hook.position === "top") {
    if (!permissions.includes(PERMISSION)) return [];
    return [
      { label: "Home", icon: <ScreenIcon name="home" />, href: "/agents/home", order: 1 },
      // My Tasks is the core-path hub (D-12) — its own nav app, right after Home.
      { label: "My Tasks", icon: <ScreenIcon name="tasks" />, href: "/agents/tasks", order: 1.05 },
      { label: "Campaigns", icon: <ScreenIcon name="campaigns" />, href: "/agents/campaigns", order: 1.1 },
      { label: "Calendar", icon: <ScreenIcon name="calendar" />, href: "/agents/calendar", order: 1.2 },
      { label: "Meetings", icon: <ScreenIcon name="meetings" />, href: "/agents/meetings", order: 1.3 },
      { label: "Reviews", icon: <ReviewsNavIcon />, href: "/agents/reviews", order: 1.4 },
    ];
  }

  // Pinned bottom screens (KB / Agent Activity / Settings). Rendered above the
  // hardcoded Support section, path-gated to /agents/*. Gated by the "agents"
  // permission for now; some of these may open up to all users later.
  if (hook.id === ListItemsHooks.APP_DRAWER && hook.position === "bottom") {
    if (!permissions.includes(PERMISSION)) return [];
    return [
      { label: "Knowledge Base", href: "/agents/knowledge-base", icon: <ScreenIcon name="kb" /> },
      // Agent Activity hidden for trial (not wired — post-trial). Route kept, no nav link.
      { label: "Settings", href: "/agents/settings", icon: <ScreenIcon name="settings" /> },
    ];
  }

  // Routes (assembled into the router by Routes.tsx → AppRoutesFromPlugins)
  if (hook.id === ListItemsHooks.ROUTES) {
    if (!permissions.includes(PERMISSION)) return [];
    return [
      { path: "/agents", element: <Home /> },
      { path: "/agents/home", element: <Home /> },
      { path: "/agents/campaigns", element: <Campaigns /> },
      { path: "/agents/calendar", element: <Calendar /> },
      { path: "/agents/meetings", element: <Meetings /> },
      { path: "/agents/tasks", element: <Tasks /> },
      { path: "/agents/digest", element: <Digest /> },
      { path: "/agents/reviews", element: <Reviews /> },
      { path: "/agents/knowledge-base", element: <KnowledgeBase /> },
      { path: "/agents/transcript-view", element: <TranscriptView /> },
      { path: "/agents/live", element: <LiveRoom /> },
      { path: "/agents/agent-activity", element: <AgentActivity /> },
      { path: "/agents/settings", element: <Settings /> },
      { path: "/agents/onboarding", element: <Onboarding /> },
      { path: "/agents/client-portal", element: <ClientPortal /> },
    ];
  }
};
